import {
  BodyText,
  Button,
  Divider,
  Flex,
  Heading,
  Tab,
  TabContent,
  Tabs,
  TabsList,
} from '@utilitywarehouse/hearth-react';
import { ChevronLeftSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';
import { ReactNode } from 'react';

import { hasServices } from '@features/EnergyTariffForm/utils';
import { EnergyQuoteType } from '@generated/gql/graphql';
import { ChargeAmount, EnergyFuelType } from '@generated/gql/graphql';
import { useSyncState } from '@hooks/useSyncState';

import { BreakdownSection } from './BreakdownSection';
import { CostBreakdown } from './CostBreakdown';
import './styles.scss';
import { EnergyBreakdownProps } from './types';

interface ListProps {
  children: ReactNode;
  marginTop?: boolean;
}

interface HeaderProps {
  quoteName: string;
  onBack?: () => void;
}

interface TabsBreakdownProps {
  quote: EnergyBreakdownProps['quote'];
  onBack?: () => void;
  children: ReactNode;
}

const defaultTabValue = 'electricity';

const List = ({ children, marginTop }: ListProps) => (
  <ul
    className={clsx(
      'energy-breakdown__list',
      marginTop && 'energy-breakdown__list--with-margin'
    )}
  >
    {children}
  </ul>
);

const BackButton = ({ onBack }: { onBack?: () => void }) => {
  if (!onBack) return null;

  return (
    <Button
      className="energy-breakdown__back-button"
      variant="ghost"
      onClick={onBack}
      data-testid="energy-breakdown-back-button"
    >
      <ChevronLeftSmallIcon />
      Back
    </Button>
  );
};

const Header = ({ quoteName, onBack }: HeaderProps) => (
  <Flex direction="column" alignItems="start" gap="200">
    <BackButton onBack={onBack} />
    <Heading className="energy-breakdown__title" size="md">
      {quoteName} tariff breakdown
    </Heading>
  </Flex>
);

const TabsBreakdown = ({ quote, onBack, children }: TabsBreakdownProps) => {
  const [activeTab, setActiveTab] = useSyncState(
    `${quote.name}-tab`,
    defaultTabValue
  );

  return (
    <>
      <Header quoteName={quote.name} onBack={onBack} />
      <Tabs
        value={activeTab}
        defaultValue={defaultTabValue}
        onValueChange={setActiveTab}
        size="md"
      >
        <TabsList>
          <Tab value="electricity" data-testid="breakdown-electricity-tab">
            Electricity
          </Tab>
          <Tab value="gas" data-testid="breakdown-gas-tab">
            Gas
          </Tab>
        </TabsList>
        {children}
      </Tabs>
    </>
  );
};

const TrackerLegal = () => (
  <>
    <Divider marginTop="200" marginBottom="200" />
    <BodyText paragraphSpacing>
      Please note that your Tracker energy tariff:
    </BodyText>
    <BodyText as="div">
      <ul className="energy-breakdown__list energy-breakdown__list--withBullets">
        <li>
          tracks the Government’s price cap at a set discount, so the prices you
          pay during the Tracker term may change when the price cap is updated;
          and
        </li>

        <li>
          is based on your chosen multi-service bundle and if you cancel any
          non-energy services, you will be transferred to the cheapest available
          Tracker energy tariff of the same version number for the remainder of
          your Tracker term.
        </li>
      </ul>
    </BodyText>
  </>
);

const EnergyBreakdown = ({ quote, fuelType, onBack }: EnergyBreakdownProps) => {
  const { hasGas, hasElectricity } = hasServices(fuelType);
  const isDualFuel = fuelType === EnergyFuelType.DualFuel;

  if (!hasElectricity && !hasGas) {
    return null;
  }

  const renderElectricity = () => {
    if (!quote.electricity || !hasElectricity) {
      return null;
    }

    return (
      <BreakdownSection quote={quote.electricity} isDualFuel={isDualFuel} />
    );
  };

  const renderGas = () => {
    if (!quote.gas || !hasGas) {
      return null;
    }

    return <BreakdownSection quote={quote.gas} isDualFuel={isDualFuel} />;
  };

  const renderCostBreakdown = () => (
    <CostBreakdown
      monthlyCost={quote.monthlyCost as ChargeAmount}
      electricity={quote.electricity ?? null}
      gas={quote.gas ?? null}
    />
  );

  const hasSingleFuel = !hasElectricity || !hasGas;

  if (hasSingleFuel) {
    return (
      <div data-testid="energy-breakdown">
        <Header quoteName={quote.name} onBack={onBack} />
        <List marginTop>
          {renderElectricity()}
          {renderGas()}
        </List>
        {renderCostBreakdown()}
        {quote.quoteType === EnergyQuoteType.Tracker && <TrackerLegal />}
      </div>
    );
  }

  return (
    <div data-testid="energy-breakdown">
      <TabsBreakdown quote={quote} onBack={onBack}>
        <TabContent value="electricity">
          <List>{renderElectricity()}</List>
          {renderCostBreakdown()}
        </TabContent>
        <TabContent value="gas">
          <List>{renderGas()}</List>
          {renderCostBreakdown()}
        </TabContent>
      </TabsBreakdown>

      {quote.quoteType === EnergyQuoteType.Tracker && <TrackerLegal />}
    </div>
  );
};

export { EnergyBreakdown };
